import React, {useCallback, useLayoutEffect, useRef, useState} from 'react';
import CollapseButton from "./collapse-button.jsx";
import Button from "../../button/button.jsx";
import {useAppShell} from "../../../app/app-shell/app-shell-context.js";

const REPORT_FILTERS_VERTICAL_OFFSET = 69;
const MENU_BOTTOM_GAP = 16;

export const minMenuHeight = 'min-h-[calc(100vh-161px)]';

const Menu = ({buttonRef, toggleFilters, fixedTop, collapsed, apply, clearSearch, children, minWidth = 240}) => {
    const {collapsed: appShellCollapsed} = useAppShell();
    const wrapperRef = useRef(null);
    const containerRef = useRef(null);
    const filtersRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [menuPosition, setMenuPosition] = useState(null);
    const [hasVerticalOverflow, setHasVerticalOverflow] = useState(false);
    const [isDateTimePickerOpen, setIsDateTimePickerOpen] = useState(false);

    const updateMenuPosition = useCallback((updateTop = true) => {
        if (!wrapperRef.current) return;

        const rect = wrapperRef.current.getBoundingClientRect();
        const nextTop = Math.max(rect.top + window.scrollY, 36);

        setMenuPosition((currentPosition) => ({
            top: updateTop ? nextTop : Math.max(currentPosition?.top ?? nextTop, 36),
            left: rect.left,
        }));
    }, []);

    const updateVerticalOverflow = useCallback(() => {
        if (!filtersRef.current) return;

        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const filtersMaxHeight = viewportHeight - REPORT_FILTERS_VERTICAL_OFFSET;
        const availableHeight = viewportHeight - Math.max(menuPosition?.top ?? 0, 0) - MENU_BOTTOM_GAP;
        const maxMenuHeight = Math.max(0, Math.min(filtersMaxHeight, availableHeight));

        setHasVerticalOverflow(filtersRef.current.scrollHeight > maxMenuHeight + 1);
    }, [menuPosition?.top]);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width-12);
            }
        });

        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    useLayoutEffect(() => {
        if (!filtersRef.current || collapsed) {
            setHasVerticalOverflow(false);
            return;
        }

        updateVerticalOverflow();

        const resizeObserver = new ResizeObserver(updateVerticalOverflow);
        resizeObserver.observe(filtersRef.current);
        if (filtersRef.current.firstElementChild) {
            resizeObserver.observe(filtersRef.current.firstElementChild);
        }

        window.addEventListener("resize", updateVerticalOverflow);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", updateVerticalOverflow);
        };
    }, [collapsed, updateVerticalOverflow]);

    useLayoutEffect(() => {
        const getHorizontalScrollContainer = () => {
            let element = wrapperRef.current?.parentElement;

            while (element) {
                const overflowX = window.getComputedStyle(element).overflowX;
                if (["auto", "scroll"].includes(overflowX)) return element;

                element = element.parentElement;
            }

            return null;
        };

        const updateFullPosition = () => updateMenuPosition();
        const updateHorizontalPosition = () => updateMenuPosition(false);

        updateMenuPosition();
        const horizontalScrollContainer = getHorizontalScrollContainer();

        window.addEventListener("resize", updateFullPosition);
        horizontalScrollContainer?.addEventListener("scroll", updateHorizontalPosition);

        return () => {
            window.removeEventListener("resize", updateFullPosition);
            horizontalScrollContainer?.removeEventListener("scroll", updateHorizontalPosition);
        };
    }, [updateMenuPosition]);

    useLayoutEffect(() => {
        updateMenuPosition(false);
        const animationFrameId = window.requestAnimationFrame(() => {
            updateMenuPosition(false);
        });

        return () => window.cancelAnimationFrame(animationFrameId);
    }, [appShellCollapsed, updateMenuPosition]);

    useLayoutEffect(() => {
        if (collapsed) return;

        updateMenuPosition();
        const animationFrameId = window.requestAnimationFrame(() => {
            updateMenuPosition();
        });

        return () => window.cancelAnimationFrame(animationFrameId);
    }, [collapsed, updateMenuPosition]);

    const handleMenuPointerDownCapture = ({target}) => {
        setIsDateTimePickerOpen(Boolean(target.closest(".sentry-date-timepicker")));
    };

    const handleMenuFocusCapture = ({target}) => {
        if (target.closest(".sentry-date-timepicker")) {
            setIsDateTimePickerOpen(true);
        }
    };

    const handleMenuBlurCapture = ({currentTarget, relatedTarget}) => {
        if (relatedTarget && currentTarget.contains(relatedTarget)) return;

        setIsDateTimePickerOpen(false);
    };

    const menuSizeClass = collapsed ? "w-0 min-w-0 max-w-0 overflow-hidden" : "w-[19.5vw] overflow-visible";
    const menuScrollClass = collapsed ? "overflow-hidden" : [hasVerticalOverflow ? "report-menu-scroll menu-scrollbar" : "", hasVerticalOverflow && isDateTimePickerOpen ? "report-menu-scroll--x-visible" : "",].filter(Boolean).join(" ");
    const menuMinWidth = collapsed ? 0 : `${minWidth}px`;
    const menuTop = Math.max(menuPosition?.top ?? 0, 0);

    return (
        <>
            <div ref={wrapperRef} style={{ minWidth: menuMinWidth }} className={`self-start mt-[-25px] print:hidden ${menuSizeClass}`}>
                <div className={`fixed z-[10] ${menuSizeClass}`}
                     style={{
                         top: `${menuPosition?.top ?? 0}px`,
                         left: `${menuPosition?.left ?? 0}px`,
                         minWidth: menuMinWidth,
                         visibility: menuPosition ? "visible" : "hidden"
                     }}>

                    <div ref={containerRef} style={{ minWidth: menuMinWidth }} className={`relative ${menuSizeClass}`}>
                        <CollapseButton buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop} collapsed={collapsed} appShellCollapsed={appShellCollapsed} menuWidth={containerWidth}/>

                        <div
                            id="filters"
                            ref={filtersRef}
                            className={menuScrollClass}
                            style={{
                                "--report-menu-top": `${menuTop}px`,
                                "--report-menu-max-height": `calc(100vh - ${REPORT_FILTERS_VERTICAL_OFFSET}px)`,
                                "--report-menu-max-height-dynamic": `calc(100dvh - ${REPORT_FILTERS_VERTICAL_OFFSET}px)`,
                            }}
                            onPointerDownCapture={handleMenuPointerDownCapture}
                            onFocusCapture={handleMenuFocusCapture}
                            onBlurCapture={handleMenuBlurCapture}
                        >

                            <div className={"card"}>
                                <div className={"card-body"}>

                                    {children}

                                    <hr className={"filter-divisor"}/>

                                    <div id="buttons" className={"flex gap-2 transform translate-y-[9px]"}>
                                        <Button title="Aplicar" onClick={apply} className={"flex-1"}>Aplicar</Button>
                                        <Button target="primary-outline" title="Limpar" onClick={clearSearch} className={"flex-1"}>Limpar</Button>
                                    </div>

                                </div>
                            </div>

                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default Menu;